import { useEffect, useRef, useState } from "react";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { compressImage } from "@/lib/image";
import { isNetworkError } from "@/lib/offline";
import { storeLocalPhoto, removeLocalPhoto, localPhotoUrl } from "@/lib/offlinePhotos";

export const photoSrc = (ph) => (ph.local ? localPhotoUrl(ph.id) : `${API}/files/${ph.id}`);
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Loader2, X, Sparkles } from "lucide-react";

const IMPUTATIONS = [
  { value: "locataire", label: "Locataire" },
  { value: "bailleur", label: "Bailleur" },
  { value: "usure_normale", label: "Usure normale" },
];

export function calcVetuste({ montant, annee_refection, duree_amortissement }, anneeConstat) {
  const m = parseFloat(montant); const d = parseInt(duree_amortissement, 10); const a = parseInt(annee_refection, 10);
  if (!(m > 0) || !(d > 0)) return { part_locataire: null, part_bailleur: null, age: null };
  const age = a ? Math.max(0, anneeConstat - a) : 0;
  const residuel = Math.max(0, 1 - age / d);
  const loc = Math.round(m * residuel * 100) / 100;
  return { part_locataire: loc, part_bailleur: Math.round((m - loc) * 100) / 100, age };
}

export default function ObservationDialog({ open, onOpenChange, observation, onChange, onSave, etatId, locked, showVetuste, anneeConstat }) {
  const [uploading, setUploading] = useState(false);
  const [table, setTable] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [filtre, setFiltre] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { if (open && !modeles.length) api.get("/settings/remarques").then((r) => setModeles(r.data)).catch(() => {}); }, [open, modeles.length]);

  useEffect(() => { if (open && showVetuste && !table.length) api.get("/settings/vetuste").then((r) => setTable(r.data)).catch(() => {}); }, [open, showVetuste, table.length]);

  if (!observation) return null;
  const set = (k, v) => onChange({ ...observation, [k]: v });
  const setVet = (patch) => {
    const next = { ...observation, ...patch };
    onChange({ ...next, ...calcVetuste(next, anneeConstat || new Date().getFullYear()) });
  };

  const addPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const added = [];
      let offline = 0;
      for (const f of Array.from(files).slice(0, 6)) {
        const small = await compressImage(f);
        try {
          const fd = new FormData(); fd.append("file", small);
          const { data } = await api.post(`/etats/${etatId}/photos`, fd);
          added.push({ id: data.id, storage_path: data.storage_path, url: data.url });
        } catch (e) {
          if (!isNetworkError(e)) throw e;
          added.push(await storeLocalPhoto(etatId, observation.numero, small)); offline++;
        }
      }
      onChange({ ...observation, photos: [...(observation.photos || []), ...added] });
      if (offline) toast.message(`${offline} photo${offline > 1 ? "s" : ""} gardée${offline > 1 ? "s" : ""} sur la tablette — envoi automatique au retour du réseau`);
      else toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} ajoutée${added.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = (ph) => {
    if (ph.local) removeLocalPhoto(ph.id); else api.delete(`/files/${ph.id}`).catch(() => {});
    onChange({ ...observation, photos: observation.photos.filter((p) => p.id !== ph.id) });
  };

  const vet = showVetuste && observation.imputation === "locataire";
  const isDefaut = observation.nature !== "usure_normale";
  const descMissing = isDefaut && !(observation.description || "").trim();
  const noPhoto = !(observation.photos || []).length;

  const elemWords = `${observation.element || ""} ${observation.piece || ""}`.toLowerCase().split(/[^a-zàâäéèêëïîôöùûüç'-]+/).filter((w) => w.length > 2);
  const scored = modeles
    .filter((m) => !filtre || m.texte.toLowerCase().includes(filtre.toLowerCase()))
    .map((m) => ({ ...m, score: elemWords.some((w) => (m.mots_cles || "").includes(w)) ? 1 : 0 }))
    .sort((a, b) => b.score - a.score);
  const insertModele = (t) => {
    const cur = (observation.description || "").trim();
    set("description", cur ? `${cur}${/[.!?]$/.test(cur) ? "" : "."} ${t}` : t);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="observation-dialog">
        <DialogHeader>
          <DialogTitle className="text-dd-blue">{isDefaut ? "Observation" : "Remarque (usure normale)"} n° {observation.numero}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-dd-warm">{observation.piece} — {observation.element}</div>
          <div>
            <Label className="text-dd-night">Commentaire {isDefaut ? <span className="text-destructive">*</span> : <span className="text-dd-warm">(facultatif)</span>}</Label>
            <Textarea data-testid="observation-description" value={observation.description || ""} disabled={locked}
              onChange={(e) => set("description", e.target.value)} rows={3}
              placeholder={isDefaut ? "Décrivez précisément le défaut constaté (obligatoire)…" : "Remarque éventuelle…"}
              className={`mt-1 ${descMissing ? "border-destructive" : ""}`} />
            {descMissing && !locked && <p className="text-xs text-destructive mt-1" data-testid="description-required">Le commentaire est obligatoire pour un défaut.</p>}
            {!locked && modeles.length > 0 && (
              <div className="mt-2" data-testid="modeles-block">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs uppercase tracking-wider text-dd-warm flex items-center gap-1"><Sparkles size={12} /> Formulations types</span>
                  <input value={filtre} onChange={(e) => setFiltre(e.target.value)} placeholder="Filtrer…" data-testid="modeles-filtre"
                    className="h-8 flex-1 max-w-[200px] rounded-md border border-dd-light px-2 text-xs bg-white" />
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
                  {scored.map((m) => (
                    <button key={m.texte} type="button" onClick={() => insertModele(m.texte)} data-testid="modele-chip"
                      className={`h-9 px-3 rounded-full text-xs border text-left ${m.score ? "border-dd-blue text-dd-blue bg-dd-blue/5" : "border-dd-light text-dd-night hover:bg-dd-light/60"}`}>
                      {m.texte}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <Label className="text-dd-night">Imputation</Label>
            <Select value={observation.imputation || ""} onValueChange={(v) => set("imputation", v)} disabled={locked}>
              <SelectTrigger className="mt-1 h-11" data-testid="observation-imputation"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
              <SelectContent>{IMPUTATIONS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {vet && (
            <div className="border border-dd-light rounded-md p-3 space-y-3" data-testid="vetuste-block">
              <div className="text-sm font-semibold text-dd-blue">Calcul de vétusté</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label className="text-dd-night text-xs">Coût remise en état (CHF)</Label>
                  <Input type="number" inputMode="decimal" value={observation.montant ?? ""} disabled={locked} data-testid="vet-montant"
                    onChange={(e) => setVet({ montant: e.target.value === "" ? null : parseFloat(e.target.value) })} className="h-11 mt-1" /></div>
                <div><Label className="text-dd-night text-xs">Année d'installation / rénovation</Label>
                  <Input type="number" inputMode="numeric" value={observation.annee_refection ?? ""} disabled={locked} data-testid="vet-annee"
                    onChange={(e) => setVet({ annee_refection: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className="h-11 mt-1" placeholder={String((anneeConstat || 2026) - 5)} /></div>
                <div><Label className="text-dd-night text-xs">Durée de vie (ans)</Label>
                  <Input type="number" inputMode="numeric" value={observation.duree_amortissement ?? ""} disabled={locked} data-testid="vet-duree"
                    onChange={(e) => setVet({ duree_amortissement: e.target.value === "" ? null : parseInt(e.target.value, 10), vetuste_libelle: "" })} className="h-11 mt-1" /></div>
              </div>
              <div>
                <Label className="text-dd-night text-xs">Tabelle paritaire (remplit la durée de vie)</Label>
                <Select value={observation.vetuste_libelle || ""} disabled={locked}
                  onValueChange={(v) => { const it = table.find((t) => t.libelle === v); setVet({ vetuste_libelle: v, duree_amortissement: it ? it.duree : observation.duree_amortissement }); }}>
                  <SelectTrigger className="mt-1 h-11" data-testid="vet-tabelle"><SelectValue placeholder="Choisir un poste…" /></SelectTrigger>
                  <SelectContent>{table.map((t) => <SelectItem key={t.libelle} value={t.libelle}>{t.libelle} — {t.duree} ans</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {observation.part_locataire != null && (
                <div className="text-sm bg-dd-light/40 rounded p-3" data-testid="vet-result">
                  Âge : <strong>{observation.age ?? 0} an{(observation.age ?? 0) > 1 ? "s" : ""}</strong> · valeur résiduelle {Math.round(Math.max(0, 1 - (observation.age || 0) / observation.duree_amortissement) * 100)} %<br />
                  Part locataire : <strong className="text-dd-blue">CHF {observation.part_locataire.toFixed(2)}</strong> · Part bailleur (vétusté) : CHF {observation.part_bailleur.toFixed(2)}
                </div>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-dd-night">Photos ({(observation.photos || []).length}) <span className="text-dd-warm">— facultatif{isDefaut ? ", fortement recommandé" : ""}</span></Label>
              {!locked && (
                <>
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="photo-input"
                    onChange={(e) => addPhotos(e.target.files)} />
                  <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}
                    className="h-11 border-dd-blue text-dd-blue" data-testid="photo-add-btn">
                    {uploading ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Camera size={16} className="mr-1" />} Prendre / ajouter une photo
                  </Button>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(observation.photos || []).map((ph) => (
                <div key={ph.id} className="relative" data-testid={`photo-${ph.id}`}>
                  <img src={photoSrc(ph)} alt="" className={`h-24 w-24 object-cover rounded-md border ${ph.local ? "border-dashed border-dd-warm" : "border-dd-light"}`} />
                  {ph.local && <span className="absolute bottom-1 left-1 right-1 text-[10px] leading-tight text-center bg-dd-night/80 text-white rounded px-1" data-testid={`photo-local-${ph.id}`}>en attente</span>}
                  {!locked && <button onClick={() => removePhoto(ph)} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center" data-testid={`photo-remove-${ph.id}`}><X size={12} /></button>}
                </div>
              ))}
              {noPhoto && <span className={`text-xs ${isDefaut ? "text-destructive" : "text-dd-warm"}`} data-testid="photo-hint">{isDefaut ? "Aucune photo : une photo du défaut est fortement recommandée comme preuve." : "Compression automatique sur la tablette (1600 px, JPEG 70 %)."}</span>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="observation-save" className="bg-dd-blue hover:bg-dd-night text-white h-11" onClick={onSave} disabled={uploading || (!locked && descMissing)}>
            {locked ? "Fermer" : "Enregistrer l'observation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
